import {Controller} from "@hotwired/stimulus"
import 'ol/ol.css';
import {Feature, Map, View} from 'ol';
import {Tile as TileLayer, Vector as VectorLayer} from 'ol/layer';
import {OSM, Vector as VectorSource} from 'ol/source';
import {fromLonLat} from 'ol/proj';
import {containsExtent, Extent} from "ol/extent";
import {Debouncer} from "../../../../js/debouncer";
import {DEFAULT_COORDINATES} from "../../../../js/constants";
import {FeatureLike} from "ol/Feature";
import {Style} from "ol/style";
import {filterToURLSearchParams} from "./filter_to_url_search_params";
import {Filter, FiltersHandler} from "./filters_handler";

export default abstract class BaseMapController<T> extends Controller {
  static targets = ["map", "checkbox"]

  declare readonly mapTarget: HTMLElement;

  protected map: Map | null = null;
  private vectorSource: VectorSource | null = null;
  private pointsLayer: VectorLayer | null = null;

  private debouncer: Debouncer<URLSearchParams, T[]> = new Debouncer<URLSearchParams, T[]>();
  private filtersHandler: FiltersHandler = new FiltersHandler((filter) => this.onFilterChanged(filter));

  connect() {
    this.filtersHandler.initialize();
    this.initMap();
  }

  initMap(): void {
    const {lng, lat} = DEFAULT_COORDINATES;

    const zoomAttributeValue = this.mapTarget.getAttribute('data-zoom');
    const zoom = zoomAttributeValue ? Number(zoomAttributeValue) : 10;

    const centerAttributeValue = this.mapTarget.getAttribute('data-center');
    const center = centerAttributeValue ? centerAttributeValue.split(',').map(Number) : [lng, lat];

    this.vectorSource = new VectorSource()
    this.pointsLayer = new VectorLayer({
      source: this.vectorSource,
      style: this.getFeatureStyle,
      // This will simplify geometries when zoomed out
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    })

    const view = new View({
      center: fromLonLat(center),
      zoom: zoom
    });

    this.map = new Map({
      target: this.mapTarget,
      layers: [
        new TileLayer({
          source: new OSM(),
          className: 'osm-tile-layer'
        }),
        this.pointsLayer
      ],
      view: view
    });

    this.map.on('moveend', () => {
      if (!this.map) {
        return;
      }

      const view = this.map.getView();
      const zoom = view.getZoom();
      const center = view.getCenter();
      const extent: Extent = view.calculateExtent(this.map.getSize());

      if (!zoom || !center || !extent) {
        return;
      }

      console.log(`PreviousZoom: ${this.filtersHandler.filter.zoom}, current: ${zoom}`);
      console.log(`PreviousExtent: ${this.filtersHandler.filter.extent}, current: ${extent}`);

      if (this.filtersHandler.filter.zoom && this.filtersHandler.filter.extent) {
        // If the zoom hasn't changed and the current extent is fully contained in the previous extent...
        if (zoom === this.filtersHandler.filter.zoom && containsExtent(this.filtersHandler.filter.extent, extent)) {
          this.filtersHandler.setMapFilter({zoom: zoom, center: center, extent: extent});
          console.log('Using cached features.');
          return; // Skip requesting new features.
        }
      }

      this.filtersHandler.setMapFilter({zoom: zoom, center: center, extent: extent});
      this.onFilterChanged(this.filtersHandler.filter).catch(console.error);
    });

    this.map.on('singleclick', (evt) => {
      const features = this.map?.getFeaturesAtPixel(evt.pixel);
      this.featureClickHandler(features || []);
    });
  }

  async onFilterChanged(filter: Filter) {
    const urlSearchParams = filterToURLSearchParams(filter);
    window.history.replaceState({}, "", `map?${urlSearchParams.toString()}`);

    try {
      const points = await this.debouncer.debounce(this.loadPoints, urlSearchParams);

      this.vectorSource?.clear()
      const features = points.map(this.pointToFeature);
      this.vectorSource?.addFeatures(features)
    } catch (error) {
      console.warn(error)
    }
  }

  disconnect() {
    this.map?.dispose();
    this.debouncer.cancelPending();
  }

  protected abstract getFeatureStyle(feature: FeatureLike): Style;
  protected abstract featureClickHandler(feature: FeatureLike[]): void;
  protected abstract loadPoints(params: URLSearchParams): Promise<T[]>;
  protected abstract pointToFeature(point: T): Feature;
}